import React from 'react';
import { useForm } from 'react-hook-form';
import { X } from 'lucide-react';
import { useProductStore } from '../../store/productStore';
import type { Product } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  productId: string | null;
}

type FormData = Omit<Product, 'id' | 'active'> & {
  type: 'main' | 'bump';
};

export const ProductModal = ({ isOpen, onClose, productId }: Props) => {
  const { register, handleSubmit, reset, watch } = useForm<FormData>();
  const { addProduct, updateProduct, getProduct } = useProductStore();

  React.useEffect(() => {
    if (productId) {
      const product = getProduct(productId);
      if (product) {
        reset({
          name: product.name,
          description: product.description,
          price: Number(product.price),
          image: product.image,
          type: product.type || 'main',
        });
      }
    } else {
      reset({
        name: '',
        description: '',
        price: 0,
        image: '',
        type: 'main',
      });
    }
  }, [productId, reset, getProduct]);

  const onSubmit = (data: FormData) => {
    const formattedData = {
      ...data,
      price: Number(data.price),
    };

    if (productId) {
      updateProduct(productId, formattedData);
    } else {
      addProduct({ ...formattedData, active: true });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {productId ? 'Edit Product' : 'Add Product'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Type
            </label>
            <select
              {...register('type')}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="main">Main Product</option>
              <option value="bump">Bump Offer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              {...register('name', { required: true })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price (₹)
            </label>
            <input
              type="number"
              step="0.01"
              {...register('price', { 
                required: true, 
                min: 0,
                valueAsNumber: true 
              })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image URL
            </label>
            <input
              type="url"
              {...register('image')}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn">
              {productId ? 'Update' : 'Add'} Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};